import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import RoomPlayer from '../components/RoomPlayer';
import RoomChat from '../components/RoomChat';
import RoomQueue from '../components/RoomQueue';

const API_URL = 'https://songmoment.onrender.com';

function formatTime(ms) {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

let ytApiLoaded = false;
let ytApiLoading = false;
const ytReadyCallbacks = [];

function loadYouTubeAPI() {
  if (ytApiLoaded) return Promise.resolve();
  if (ytApiLoading) return new Promise(r => ytReadyCallbacks.push(r));
  ytApiLoading = true;
  return new Promise(resolve => {
    ytReadyCallbacks.push(resolve);
    if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(tag);
    }
    window.onYouTubeIframeAPIReady = () => {
      ytApiLoaded = true; ytApiLoading = false;
      ytReadyCallbacks.forEach(cb => cb());
      ytReadyCallbacks.length = 0;
    };
  });
}

// ── Song Search Component ──
function SongSearch({ socket, roomId, username }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [addedId, setAddedId] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const searchSongs = async (q) => {
    if (!q.trim()) { setResults([]); return; }
    setSearching(true);
    try {
      const res = await axios.get(`${API_URL}/api/spotify/search?q=${encodeURIComponent(q)}`);
      setResults(res.data || []);
      setShowResults(true);
    } catch (err) {
      console.error('Search error:', err);
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleInput = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchSongs(val), 500);
  };

  const addToQueue = (track) => {
    if (!socket) return;
    const trackId = track.youtube_id || track.id || `track_${Date.now()}`;
    socket.emit('room_queue_add', {
      roomId,
      username,
      moment: {
        id: `search_${Date.now()}`,
        track_name: track.track_name || track.name,
        artist_name: track.artist_name || track.artist,
        album_image: track.album_image || track.image,
        timestamp_ms: 0,
        spotify_track_id: track.youtube_id ? `youtube_${track.youtube_id}` : null,
        embed_url: track.embed_url || null,
      },
    });
    setAddedId(trackId);
    setTimeout(() => setAddedId(null), 2000);
    setQuery('');
    setResults([]);
    setShowResults(false);
  };

  return (
    <div ref={searchRef} style={{ position: 'relative', marginBottom: 12 }}>
      <style>{`
        .ss-input:focus { border-color: rgba(212,83,126,0.6) !important; outline: none; }
        .ss-result:hover { background: rgba(212,83,126,0.08) !important; }
      `}</style>

      <div style={{ position: 'relative' }}>
        <input
          className="ss-input"
          value={query}
          onChange={handleInput}
          onFocus={() => results.length > 0 && setShowResults(true)}
          placeholder="🔍 Search songs to add to queue..."
          style={{
            width: '100%', boxSizing: 'border-box',
            background: 'rgba(255,255,255,0.04)',
            border: '0.5px solid rgba(212,83,126,0.25)',
            borderRadius: 12, padding: '11px 40px 11px 14px',
            fontSize: 13, color: '#f5e6ed',
            transition: 'border-color 0.2s',
          }}
        />
        {searching && (
          <div style={{
            position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
            width: 16, height: 16, borderRadius: '50%',
            border: '2px solid rgba(212,83,126,0.3)',
            borderTopColor: '#D4537E',
            animation: 'spin 0.8s linear infinite',
          }} />
        )}
        {query && !searching && (
          <button
            onClick={() => { setQuery(''); setResults([]); setShowResults(false); }}
            style={{
              position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
              background: 'none', border: 'none', color: 'rgba(245,230,237,0.3)',
              fontSize: 16, cursor: 'pointer', padding: 2,
            }}>✕</button>
        )}
      </div>

      {/* Results dropdown */}
      {showResults && results.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
          background: '#0f0810',
          border: '0.5px solid rgba(212,83,126,0.25)',
          borderRadius: 12, marginTop: 6,
          maxHeight: 320, overflowY: 'auto',
          boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(212,83,126,0.2) transparent',
        }}>
          {results.map((track, i) => {
            const trackId = track.youtube_id || track.id;
            const isAdded = addedId === trackId;
            return (
              <div
                key={i}
                className="ss-result"
                onClick={() => addToQueue(track)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 12px',
                  borderBottom: i < results.length - 1 ? '0.5px solid rgba(255,255,255,0.04)' : 'none',
                  cursor: 'pointer',
                  background: isAdded ? 'rgba(34,197,94,0.08)' : 'transparent',
                  transition: 'background 0.15s',
                }}
              >
                {(track.album_image || track.image) ? (
                  <img
                    src={track.album_image || track.image} alt=""
                    style={{ width: 38, height: 38, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }}
                  />
                ) : (
                  <div style={{
                    width: 38, height: 38, borderRadius: 6, flexShrink: 0,
                    background: 'rgba(212,83,126,0.1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 16,
                  }}>♪</div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 13, color: isAdded ? '#22c55e' : '#f5e6ed',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    marginBottom: 2,
                  }}>
                    {track.track_name || track.name}
                  </div>
                  <div style={{
                    fontSize: 11, color: 'rgba(245,230,237,0.4)',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {track.artist_name || track.artist}
                  </div>
                </div>
                <div style={{
                  flexShrink: 0, fontSize: 11,
                  color: isAdded ? '#22c55e' : 'rgba(212,83,126,0.7)',
                  letterSpacing: '0.06em',
                }}>
                  {isAdded ? '✓ Added' : '+ Add'}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showResults && results.length === 0 && !searching && query.trim() && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
          background: '#0f0810', border: '0.5px solid rgba(212,83,126,0.2)',
          borderRadius: 12, marginTop: 6, padding: '16px',
          textAlign: 'center', fontSize: 12,
          color: 'rgba(245,230,237,0.3)',
        }}>
          No results found for "{query}"
        </div>
      )}
    </div>
  );
}

// ── Main RoomPage ──
export default function RoomPage() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { user, socket } = useApp();

  const [room, setRoom] = useState(null);
  const [error, setError] = useState('');
  const [joined, setJoined] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [ready, setReady] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [chat, setChat] = useState([]);
  const [reactions, setReactions] = useState([]);
  const [copied, setCopied] = useState(false);

  const playerRef = useRef(null);
  const containerRef = useRef(null);
  const intervalRef = useRef(null);
  const isPlayingRef = useRef(false);
  const currentIndexRef = useRef(0);
  const roomRef = useRef(null);

  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);
  useEffect(() => { currentIndexRef.current = currentIndex; }, [currentIndex]);
  useEffect(() => { roomRef.current = room; }, [room]);

  const username = user?.username || user?.user_metadata?.username || user?.email;
  const currentMoment = room?.queue?.[currentIndex];

  const getYoutubeId = (moment) => {
    if (moment?.spotify_track_id?.startsWith('youtube_'))
      return moment.spotify_track_id.replace('youtube_', '');
    if (moment?.embed_url?.includes('youtube'))
      return moment.embed_url.split('/embed/')[1]?.split('?')[0];
    return null;
  };

  useEffect(() => {
    if (!socket || !user) return;
    socket.emit('room_join', { roomId, username });

    socket.on('room_state', (state) => {
      setRoom(state);
      setCurrentIndex(state.currentIndex || 0);
      setIsPlaying(state.isPlaying || false);
      setCurrentTime(state.currentTime || 0);
      setChat(state.chat || []);
      setJoined(true);
    });

    socket.on('room_error', ({ message }) => setError(message));
    socket.on('room_member_joined', ({ members }) => setRoom(prev => prev ? { ...prev, members } : prev));
    socket.on('room_member_left', ({ members }) => setRoom(prev => prev ? { ...prev, members } : prev));

    socket.on('room_play', ({ currentTime, currentIndex }) => {
      setIsPlaying(true);
      setCurrentTime(currentTime);
      if (currentIndex !== undefined) setCurrentIndex(currentIndex);
      if (playerRef.current?.seekTo) {
        playerRef.current.seekTo(currentTime, true);
        playerRef.current.playVideo();
      }
    });

    socket.on('room_pause', ({ currentTime }) => {
      setIsPlaying(false);
      setCurrentTime(currentTime);
      if (playerRef.current?.pauseVideo) playerRef.current.pauseVideo();
    });

    socket.on('room_seek', ({ currentTime }) => {
      setCurrentTime(currentTime);
      if (playerRef.current?.seekTo) playerRef.current.seekTo(currentTime, true);
    });

    socket.on('room_change_track', ({ currentIndex }) => {
      setCurrentIndex(currentIndex);
      setCurrentTime(0);
      setIsPlaying(true);
      setReady(false);
    });

    socket.on('room_queue_update', ({ queue }) => {
      setRoom(prev => prev ? { ...prev, queue } : prev);
    });

    socket.on('room_chat', (msg) => {
      setChat(prev => [...prev, msg]);
    });

    socket.on('room_reaction', ({ username, emoji }) => {
      const id = Date.now();
      setReactions(prev => [...prev, { id, username, emoji }]);
      setTimeout(() => setReactions(prev => prev.filter(r => r.id !== id)), 2000);
    });

    socket.on('room_sync', ({ currentTime, currentIndex, isPlaying }) => {
      setCurrentTime(currentTime);
      setCurrentIndex(currentIndex);
      setIsPlaying(isPlaying);
      if (playerRef.current?.seekTo) playerRef.current.seekTo(currentTime, true);
    });

    return () => {
      socket.emit('room_leave', { roomId, username });
      socket.off('room_state');
      socket.off('room_error');
      socket.off('room_member_joined');
      socket.off('room_member_left');
      socket.off('room_play');
      socket.off('room_pause');
      socket.off('room_seek');
      socket.off('room_change_track');
      socket.off('room_queue_update');
      socket.off('room_chat');
      socket.off('room_reaction');
      socket.off('room_sync');
    };
  }, [socket, user, roomId]);

  useEffect(() => {
    if (!currentMoment) return;
    const videoId = getYoutubeId(currentMoment);
    if (!videoId) return;
    const startSeconds = Math.floor((currentMoment.timestamp_ms || 0) / 1000);

    loadYouTubeAPI().then(() => {
      if (!containerRef.current) return;
      if (playerRef.current?.destroy) playerRef.current.destroy();
      setReady(false);
      setDuration(0);

      playerRef.current = new window.YT.Player(containerRef.current, {
        videoId,
        playerVars: {
          start: startSeconds, autoplay: 1,
          controls: 0, disablekb: 1, fs: 0,
          modestbranding: 1, rel: 0, playsinline: 1,
        },
        events: {
          onReady: (e) => {
            setReady(true);
            const check = setInterval(() => {
              const d = e.target.getDuration();
              if (d > 0) { setDuration(d); clearInterval(check); }
            }, 300);
            if (currentTime > 0) e.target.seekTo(currentTime, true);
          },
          onStateChange: (e) => {
            if (e.data === window.YT.PlayerState.PLAYING) {
              setIsPlaying(true);
              intervalRef.current = setInterval(() => {
                if (playerRef.current?.getCurrentTime) {
                  setCurrentTime(playerRef.current.getCurrentTime());
                }
              }, 500);
            } else if (e.data === window.YT.PlayerState.ENDED) {
              setIsPlaying(false);
              clearInterval(intervalRef.current);
              const nextIndex = (currentIndexRef.current + 1) % (roomRef.current?.queue?.length || 1);
              socket?.emit('room_change_track', { roomId, currentIndex: nextIndex });
            } else {
              setIsPlaying(false);
              clearInterval(intervalRef.current);
            }
          },
        },
      });
    });

    return () => clearInterval(intervalRef.current);
  }, [currentIndex, currentMoment?.id]);

  const isHost = room?.host === username;

  const togglePlay = () => {
    if (!playerRef.current || !ready) return;
    const t = playerRef.current.getCurrentTime();
    if (isPlaying) {
      playerRef.current.pauseVideo();
      socket?.emit('room_pause', { roomId, currentTime: t });
    } else {
      playerRef.current.playVideo();
      socket?.emit('room_play', { roomId, currentTime: t, currentIndex });
    }
  };

  const handleSeek = (e) => {
    const t = Number(e.target.value);
    setCurrentTime(t);
    if (playerRef.current?.seekTo) playerRef.current.seekTo(t, true);
    socket?.emit('room_seek', { roomId, currentTime: t });
  };

  const changeTrack = (index) => {
    socket?.emit('room_change_track', { roomId, currentIndex: index });
  };

  const copyLink = () => {
    navigator.clipboard.writeText(`https://songmoment.pages.dev/room/${roomId}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!user) return (
    <div style={{ minHeight: '100vh', background: '#080608', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, color: '#f5e6ed', marginBottom: 16 }}>Sign in to join the room</p>
        <button onClick={() => navigate('/')} style={{ background: '#D4537E', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 24px', fontSize: 14, cursor: 'pointer' }}>Go to feed</button>
      </div>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', background: '#080608', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>😕</div>
        <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, color: '#f5e6ed', marginBottom: 8 }}>{error}</p>
        <button onClick={() => navigate('/')} style={{ background: '#D4537E', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 24px', fontSize: 14, cursor: 'pointer' }}>Go to feed</button>
      </div>
    </div>
  );

  if (!joined) return (
    <div style={{ minHeight: '100vh', background: '#080608', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <p style={{ fontSize: 11, color: 'rgba(245,230,237,0.35)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Joining room...</p>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#080608', display: 'flex', flexDirection: 'column' }}>

      {/* Hidden YouTube iframe */}
      <div style={{ position: 'fixed', top: '-9999px', left: '-9999px', width: 320, height: 180, pointerEvents: 'none' }}>
        <div ref={containerRef} style={{ width: 320, height: 180 }} />
      </div>

      {/* Floating reactions */}
      <div style={{ position: 'fixed', bottom: 100, right: 20, zIndex: 500, display: 'flex', flexDirection: 'column', gap: 8, pointerEvents: 'none' }}>
        {reactions.map(r => (
          <div key={r.id} style={{ fontSize: 28, animation: 'floatUp 2s ease-out forwards' }}>
            {r.emoji}
          </div>
        ))}
      </div>

      <style>{`
        @keyframes floatUp {
          0% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-80px); }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      {/* Header */}
      <header style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: 'rgba(8,6,8,0.95)', backdropFilter: 'blur(20px)',
        borderBottom: '0.5px solid rgba(212,83,126,0.2)', padding: '0 20px',
      }}>
        <div style={{ maxWidth: 900, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 58 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', color: 'rgba(245,230,237,0.4)', fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer' }}>← Feed</button>
            <div style={{ width: 1, height: 16, background: 'rgba(212,83,126,0.2)' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 8px #22c55e' }} />
              <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 16, color: '#f5e6ed', letterSpacing: '0.05em' }}>
                Room · {roomId}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {room?.members?.slice(0, 4).filter(m => m?.username).map(m => (
                <div key={m.username} title={m.username} style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: `hsl(${m.username.charCodeAt(0) * 15}, 60%, 35%)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 700, color: '#fff', border: '1.5px solid #080608',
                }}>
                  {m.username[0].toUpperCase()}
                </div>
              ))}
              {room?.members?.length > 4 && (
                <span style={{ fontSize: 11, color: 'rgba(245,230,237,0.4)' }}>+{room.members.length - 4}</span>
              )}
            </div>
            <button onClick={copyLink} style={{
              fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase',
              color: copied ? '#22c55e' : 'rgba(245,230,237,0.5)',
              background: 'none', border: `0.5px solid ${copied ? '#22c55e' : 'rgba(212,83,126,0.3)'}`,
              borderRadius: 20, padding: '5px 14px', cursor: 'pointer',
            }}>
              {copied ? '✓ Copied!' : '🔗 Invite'}
            </button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <div style={{ flex: 1, maxWidth: 900, margin: '0 auto', width: '100%', padding: '24px 20px', display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20 }}>

        {/* Left — Player + Search + Queue */}
        <div>
          <RoomPlayer
            currentMoment={currentMoment}
            isPlaying={isPlaying}
            currentTime={currentTime}
            duration={duration}
            ready={ready}
            isHost={isHost}
            currentIndex={currentIndex}
            queueLength={room?.queue?.length || 0}
            onTogglePlay={togglePlay}
            onSeek={handleSeek}
            onPrev={() => changeTrack((currentIndex - 1 + (room?.queue?.length || 1)) % (room?.queue?.length || 1))}
            onNext={() => changeTrack((currentIndex + 1) % (room?.queue?.length || 1))}
          />

          {/* ── Song Search ── */}
          <div style={{ marginTop: 20 }}>
            <div style={{ fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(212,83,126,0.6)', marginBottom: 8 }}>
              Add to Queue
            </div>
            <SongSearch socket={socket} roomId={roomId} username={username} />
          </div>

          <RoomQueue
            queue={room?.queue || []}
            currentIndex={currentIndex}
            isHost={isHost}
            username={username}
            socket={socket}
            roomId={roomId}
            onChangeTrack={changeTrack}
          />
        </div>

        {/* Right — Chat */}
        <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)', position: 'sticky', top: 78 }}>
          <RoomChat
            chat={chat}
            members={room?.members || []}
            host={room?.host}
            username={username}
            onSendMessage={(msg) => socket?.emit('room_chat', { roomId, username, message: msg })}
            onSendReaction={(emoji) => socket?.emit('room_reaction', { roomId, username, emoji })}
          />
        </div>

      </div>
    </div>
  );
}